import * as PIXI from 'pixi.js-legacy';
import PolyMask from './PolyMask';
import {
  calculateFit,
  isDefined,
  isVideo,
  preloadVideo,
  toPixel,
} from '../utils';
import gsap from '../../gsap';

export default class Mask {
  maskFrame;
  currentGraphics = [];
  callbacks = [];
  textureArray = [];

  constructor(app, grids, assets, blurStrength = 8) {
    this.assets = assets;

    this.assets.forEach((asset, i) => {
      if (!asset.name) asset.name = i.toString();
    });

    this.grids = grids;
    this.app = app;
    this.container = new PIXI.Container();
    this.container.interactiveChildren = false;
    this.app.stage.addChildAt(this.container, 0);
    this.textures = {};
    this.width = this.app.screen.width;
    this.height = this.app.screen.height;
    this.blurFilter = new PIXI.filters.BlurFilter(blurStrength);
    this.blurFilter.repeatEdgePixels = true;

    this.assets.forEach((asset, index) => {
      if (asset.pixi) {
        this.textures[index] = PIXI.RenderTexture.create({
          width: this.width,
          height: this.height,
        });
      }
    });
  }

  destroy() {
    this.container.destroy();
    PIXI.Loader.shared.reset();
  }

  pause() {
    this.removeAllTickers(this.currentGraphics);
  }

  async draw(sequence, delay = 0) {
    this.maskFrame = sequence.mask;
    this.duration = sequence.duration;

    this.textureArray.forEach(texture => {
      if (texture.baseTexture.resource.autoUpdate)
        texture.baseTexture.resource.autoUpdate = false;
    });

    return new Promise(resolve => {
      setTimeout(async () => {
        this.updateMasks();
        this.callbacks.forEach(({ index, callback }) =>
          this._addCallback(index, callback),
        );
        resolve();
      }, delay);
    });
  }

  removeAllTickers(graphics) {
    graphics.forEach(({ mask }) => mask.removeTicker());
  }

  getAssetPositioning(
    graphic,
    mask,
    pin,
    assetStartPosition,
    alignToMask,
    width,
    height,
    coverFill,
    assetWidth,
    assetHeight,
    scaleStart = 1,
    initialHeight,
    initialWidth,
  ) {
    let newHeight,
      newWidth,
      startX,
      startY,
      anchor = [0, 0],
      scale = scaleStart;

    const tempStart = assetStartPosition
      ? [
          toPixel(assetStartPosition[0], width),
          toPixel(assetStartPosition[1], height),
        ]
      : [0, 0];

    switch (pin) {
      case 'top':
        startX = tempStart[0];
        startY = alignToMask ? mask.maskInfo.y : tempStart[1];
        break;
      case 'left':
        startX = alignToMask ? mask.maskInfo.x : tempStart[0];
        startY = tempStart[1];
        break;
      case 'bottom':
        startX = tempStart[0];
        startY = alignToMask ? mask.maskInfo.y2 : height;
        anchor = [0.5, 1];
        break;
      case 'right':
        startX = alignToMask ? mask.maskInfo.x2 : width;
        startY = tempStart[1];
        anchor = [1, 0.5];
        break;
      case 'center':
        startX = alignToMask
          ? (mask.maskInfo.x2 + mask.maskInfo.x) / 2
          : width / 2;
        startY = alignToMask
          ? (mask.maskInfo.y2 + mask.maskInfo.y) / 2
          : height / 2;
        anchor = [0.5, 0.5];
        break;
      default:
        startX = tempStart[0];
        startY = tempStart[1];
        break;
    }

    if (assetWidth) {
      newWidth = toPixel(assetWidth, width);
    } else if (assetHeight) {
      newHeight = toPixel(assetHeight, height);
    }

    if (coverFill) {
      anchor = [0, 0];
      const fitTo = alignToMask
        ? {
            ...mask.maskInfo,
            height: mask.maskInfo.maxHeight,
            width: mask.maskInfo.maxWidth,
          }
        : { x: 0, y: 0, width, height };

      const fitFrom = {
        x: 0,
        y: 0,
        width: initialWidth,
        height: initialHeight,
      };
      const cover = calculateFit(fitFrom, fitTo, true);
      let coverX = alignToMask ? cover.left + fitTo.x : cover.left;
      let coverY = alignToMask ? cover.top + fitTo.y : cover.top;

      if (pin) {
        if (coverX < 0 && (pin === 'left' || pin === 'right')) {
          coverX = pin === 'left' ? 0 : coverX * 2;
        }
        if (coverY < 0 && (pin === 'top' || pin === 'bottom')) {
          coverY = pin === 'top' ? 0 : coverY * 2;
        }
      }
      startX = coverX;
      startY = coverY;
      scale = cover.scale;

      newHeight = cover.height;
      newWidth = cover.width;
    }

    graphic.height = newHeight;
    graphic.width = newWidth;
    graphic.anchor.set(anchor[0], anchor[1]);
    graphic.position.set(startX, startY);
    graphic.scale.set(scale);

    return {
      height: newHeight,
      width: newWidth,
      startX,
      startY,
      anchor,
      scale,
    };
  }

  createGraphicFromAsset(index) {
    let texture = !isNaN(index)
      ? this.textureArray[index]
      : this.textureArray.find(texture => texture.name === index);
    let graphic, pixiContainer, height, width;

    if (texture) {
      height =
        texture.baseTexture.resource.source.height ||
        texture.baseTexture.resource.source.offsetHeight;
      width =
        texture.baseTexture.resource.source.width ||
        texture.baseTexture.resource.source.offsetWidth;

      if (typeof texture.baseTexture.resource.source.play === 'function') {
        texture.baseTexture.resource.source.play();
        texture.baseTexture.resource.autoUpdate = true;
      }
      graphic = PIXI.Sprite.from(texture);
    } else {
      if (!isDefined(this.assets[index]))
        console.error(`Missing asset ${index}`);
      // Pixi Scene
      pixiContainer = this.assets[index].pixi;
      graphic = new PIXI.Sprite(this.textures[index]);
      height = pixiContainer.height;
      width = pixiContainer.width;
    }
    return {
      graphic,
      pixiContainer,
      height: height,
      width: width,
    };
  }

  async updateMasks() {
    const oldChildren = [...this.container.children];
    const oldGraphics = [...this.currentGraphics];
    this.removeAllTickers(oldGraphics);
    this.currentGraphics = [];

    if (!this.maskFrame) return;

    const height = this.height;
    const width = this.width;

    // Create the poly masks
    const masks = this.maskFrame.map(
      ({
        grid: gridIndex = 0,
        horizontal,
        vertical,
        circle,
        padding = 0,
        polyOnly = false,
        ...rest
      }) => {
        const grid = this.grids[gridIndex];
        const mask = new PolyMask(
          this.app,
          grid,
          { horizontal, vertical, circle },
          padding,
          polyOnly,
        );
        return { mask: mask, polyOnly, ...rest };
      },
    );

    // Add Textures to the poly mask graphics - Image, Video, PIXI Container
    this.currentGraphics = masks.map(
      ({
        mask,
        asset: assetIndex,
        assetWidth,
        assetHeight,
        container = 'canvas',
        coverFill,
        scaleStart,
        scaleWithMask,
        pin,
        assetStartPosition,
        assetPositionWithMask = false,
        polyOnly,
        filter,
        colorFill,
        fadeIn,
        fadeOut,
        ...rest
      }) => {
        const alignToMask = container === 'mask';
        const maskWidth = alignToMask ? mask.maskInfo.width : width;
        const maskHeight = alignToMask ? mask.maskInfo.height : height;

        if (polyOnly) return { mask, polyOnly, ...rest };

        if (colorFill) {
          const graphic = new PIXI.Graphics();
          const color = Math.floor(Math.random() * 16777215);
          graphic.beginFill(color);
          graphic.drawRect(0, 0, width, height);
          graphic.endFill();
          this.container.addChild(graphic);
          graphic.mask = mask.sprite;
          return { mask, graphic, ...rest };
        }

        this.container.addChild(mask.sprite);

        const {
          graphic,
          pixiContainer,
          height: _height,
          width: _width,
        } = this.createGraphicFromAsset(assetIndex);

        graphic.alpha = fadeIn ? 0 : 1;

        const graphicContainer = new PIXI.Container();
        graphicContainer.addChild(graphic);
        graphicContainer.mask = mask.sprite;
        this.container.addChild(graphicContainer);

        if (filter === 'blur') {
          graphic.filters = [this.blurFilter];
          const blurTint = new PIXI.Graphics();
          blurTint.beginFill(0xffffff, 0.5);
          blurTint.drawRect(0, 0, this.width, this.height);
          blurTint.endFill();
          graphicContainer.addChild(blurTint);
        }

        const { startX, startY } = this.getAssetPositioning(
          graphic,
          mask,
          pin,
          assetStartPosition,
          alignToMask,
          maskWidth,
          maskHeight,
          coverFill,
          assetWidth,
          assetHeight,
          scaleStart,
          _height,
          _width,
        );

        const moveX = !['left', 'right', 'center'].includes(pin);
        const moveY = !['top', 'bottom', 'center'].includes(pin);
        mask.onUpdate = coordinates => {
          if (assetPositionWithMask) {
            graphic.anchor.set(0);
            graphic.position.set(coordinates.left, coordinates.top);
          } else {
            if (pin === 'center') {
              graphic.x = coordinates.center.x;
              graphic.y = coordinates.center.y;
            } else {
              if (!moveX) {
                graphic.x = coordinates[pin];
              }
              if (!moveY) {
                graphic.y = coordinates[pin];
              }
            }

            if (coverFill && container !== 'canvas') {
              if (scaleWithMask) {
                try {
                  const cover = calculateFit(
                    graphic,
                    {
                      width: coordinates.width,
                      height: coordinates.height,
                      x: 0,
                      y: 0,
                    },
                    true,
                  );

                  graphic.width = cover.width;
                  graphic.height = cover.height;
                  let coverX = cover.left + coordinates.left;
                  let coverY = coordinates.top + cover.top;

                  if (pin) {
                    if (coverX < 0 && (pin === 'left' || pin === 'right')) {
                      coverX = pin === 'left' ? 0 : coverX * 2;
                    }
                    if (coverY < 0 && (pin === 'top' || pin === 'bottom')) {
                      coverY = pin === 'top' ? 0 : coverY;
                    }
                  }

                  graphic.position.set(coverX, coverY);
                } catch (e) {
                  return;
                }
                // graphic.scale.set(cover.scale);
              } else {
                if (graphic.x > coordinates.left) graphic.x = coordinates.left;
                if (graphic.y > coordinates.top) graphic.y = coordinates.top;

                if (graphic.x + graphic.width < coordinates.right)
                  graphic.x = coordinates.right - graphic.width;
                if (graphic.y + graphic.height < coordinates.bottom)
                  graphic.y = coordinates.bottom - graphic.height;
              }
            }
          }

          if (pixiContainer) {
            this.app.renderer.render(pixiContainer, this.textures[assetIndex]);
          }
        };

        return {
          mask,
          graphic,
          startX,
          startY,
          moveX,
          moveY,
          container,
          assetStartPosition,
          fadeIn,
          fadeOut,
          ...rest,
        };
      },
    );

    setTimeout(() => {
      oldChildren.forEach(child => {
        this.container.removeChild(child);
        try {
          child.destroy();
        }catch (e){}
      });
      oldGraphics.forEach(({ mask }) => {
        mask.destroy();
      });
    }, 50); // Slight delay to removal so there is no flash

    const p1 = this.currentGraphics.map(
      ({
        graphic,
        mask,
        container,
        startX,
        startY,
        moveX,
        moveY,
        assetStartPosition,
        assetEndPosition,
        moveDelay,
      }) => {
        const alignToMask = container === 'mask';
        const maskWidth = alignToMask ? mask.maskInfo.width : this.width;
        const maskHeight = alignToMask ? mask.maskInfo.height : this.height;

        if (!assetEndPosition) return;
        const [endX, endY] = assetEndPosition;

        const newX =
          assetStartPosition[0] > assetEndPosition[0]
            ? startX - Math.abs(toPixel(endX, maskWidth))
            : startX + toPixel(endX, maskWidth);
        const newY =
          assetStartPosition[1] > assetEndPosition[1]
            ? startY - Math.abs(toPixel(endY, maskHeight))
            : startY + toPixel(endY, maskHeight);

        const x = moveX ? newX : undefined;
        const y = moveY ? newY : undefined;

        return this.move(graphic, { x, y }, moveDelay);
      },
    );

    const p2 = this.currentGraphics.map(({ graphic, scaleEnd }) => {
      if (isDefined(scaleEnd)) return this.scale(graphic, scaleEnd);
    });

    const p3 = this.currentGraphics.map(
      ({ graphic, fadeIn, fadeOut, fadeDelay }) => {
        if (fadeIn) this.fade(graphic, false, fadeDelay);
        else if (fadeOut) this.fade(graphic, true, fadeDelay);
      },
    );

    return Promise.all([...p1, ...p2, ...p3]);
  }

  async move(graphic, { x, y }, delay = 0) {
    return new Promise(resolve => {
      gsap.to(graphic, {
        x,
        y,
        ease: 'nikeEase',
        duration: this.duration / 1000,
        onComplete: resolve,
        delay: delay / 1000,
      });
    });
  }

  async scale(graphic, scaleVal, delay = 0) {
    return new Promise(resolve => {
      gsap.to(graphic.scale, {
        x: scaleVal,
        y: scaleVal,
        ease: 'nikeEase',
        duration: this.duration / 1000,
        onComplete: resolve,
        delay: delay / 1000,
      });
    });
  }

  async fade(graphic, out = false, delay = 0) {
    return new Promise(resolve => {
      gsap.to(graphic, {
        alpha: out ? 0 : 1,
        ease: 'nikeEase',
        duration: this.duration / 1000,
        onComplete: resolve,
        delay: delay / 1000,
      });
    });
  }

  resetVideos() {
    this.textureArray.forEach(texture => {
      if (texture.baseTexture.resource.source.tagName === 'VIDEO') {
        texture.baseTexture.resource.source.pause();
        texture.baseTexture.resource.source.currentTime = 0;
      }
    });
  }

  async loadAssets() {
    const videoTypes = ['mp4', '3gp', 'ogg', 'webm'];
    await new Promise(resolve => {
      const loader = PIXI.Loader.shared;
      loader.reset();
      const videos = this.assets.filter(asset => {
        if (!asset.url) return false;
        return isVideo(asset.url);
      });

      this.assets.forEach((asset, index) => {
        if (asset.url && !videoTypes.find(type => asset.url.includes(type)))
          loader.add(asset.name || index.toString(), asset.url);
      });

      loader.load(async (loader, resources) => {
        const vids = await Promise.all(
          videos.map(async ({ name, url }) => {
            const element = document.createElement('video');
            await preloadVideo(element, url);
            return { type: 5, data: element, name, url };
          }),
        );
        this.resourcesArray = this.assets.map(val => {
          const { url, pixi } = val;
          if (pixi) return val;
          return [...Object.values(resources), ...vids].find(
            ({ url: _url }) => url === _url,
          );
        });
        this.resources = this.resourcesArray.reduce((total, next, index) => {
          return { ...total, [next.name || index.toString()]: next };
        }, {});

        this.textureArray = this.resourcesArray
          .filter(resource => !isDefined(resource.pixi))
          .map(resource => {
            if (resource.texture) {
              resource.texture.name = resource.name;
              return resource.texture;
            }

            const texture = PIXI.Texture.from(resource.data);
            texture.baseTexture.resource.source.muted = true;
            texture.baseTexture.resource.source.loop = true;
            texture.baseTexture.resource.autoPlay = false;
            texture.baseTexture.resource.updateFPS = 30;
            texture.name = resource.name;
            return texture;
          });

        setTimeout(resolve, 1000); // Give time for render loop to initiate textures
      });
    });

    this.resetVideos();
  }

  getMaskBounds(index) {
    if (typeof index === 'number') {
      if (!this.currentGraphics[index]) return;
      return this.currentGraphics[index].mask.maskInfo;
    } else {
      const graphic = this.currentGraphics.find(g => g.name === index);
      if (!graphic) return;
      return graphic.mask.maskInfo;
    }
  }

  _addCallback(index, callback) {
    if (typeof index === 'number') {
      if (!this.currentGraphics[index]) return;
      this.currentGraphics[index].mask.callback = callback;
    } else {
      const graphic = this.currentGraphics.find(g => g.name === index);

      if (!graphic) return;

      graphic.mask.callback = callback;
    }
  }

  /**
   * Add callback to a mask - returns points for polygon -- only work with horizontal and vertical line masks. No circle or mixed masks
   * @constructor
   * @param {number | string} index
   * @param {function} callback - function is called with polygon points
   */
  addCallback(index, callback) {
    if (!this.callbacks.find(c => c.index === index)) {
      this.callbacks.push({ index, callback });
    }
    this._addCallback(index, callback);
  }

  _removeCallback(index) {
    if (typeof index === 'number') {
      if (!this.currentGraphics[index]) return;

      this.currentGraphics[index].mask.callback = null;
    } else {
      this.currentGraphics = this.currentGraphics.filter(g => {
        if (g.name === index) {
          g.mask.callback = null;
        }

        return g.name !== index;
      });
    }
  }

  /**
   * Remove a callback from a mask
   *
   * @param {string | number} index
   */
  removeCallback(index) {
    this.callbacks = this.callbacks.filter(callback => {
      return callback.index !== index;
    });

    this._removeCallback(index);
  }
}
